import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface TerminalDepartureOptions {
  reducedMotion: boolean;
}

interface VideoScrubber {
  setProgress(progress: number): void;
  load(): void;
}

type OperationPhase =
  | 'arrival'
  | 'identification'
  | 'route'
  | 'handling'
  | 'vision'
  | 'scale'
  | 'dispatch';

const phaseStops: Array<{ phase: OperationPhase; start: number }> = [
  { phase: 'arrival', start: 0 },
  { phase: 'identification', start: 0.18 },
  { phase: 'route', start: 0.35 },
  { phase: 'handling', start: 0.48 },
  { phase: 'vision', start: 0.67 },
  { phase: 'scale', start: 0.78 },
  { phase: 'dispatch', start: 0.91 },
];

function createVideoScrubber(video: HTMLVideoElement): VideoScrubber {
  let duration = 0;
  let progress = 0;
  let desiredTime = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration) ? Math.max(0, video.duration - 1 / 48) : 0;
    desiredTime = duration * progress;
    video.pause();
  };
  video.addEventListener('loadedmetadata', syncMetadata);

  const render = (): void => {
    if (duration > 0 && !video.seeking && Math.abs(video.currentTime - desiredTime) > 1 / 60) {
      video.currentTime = desiredTime;
    }
    requestAnimationFrame(render);
  };
  requestAnimationFrame(render);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) video.pause();
  });

  return {
    load(): void {
      if (video.preload === 'auto') return;
      video.preload = 'auto';
      video.load();
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();
    },
    setProgress(nextProgress: number): void {
      progress = gsap.utils.clamp(0, 1, nextProgress);
      if (duration > 0) desiredTime = duration * progress;
    },
  };
}

function resolvePhase(progress: number): OperationPhase {
  let phase: OperationPhase = 'arrival';
  for (const stop of phaseStops) {
    if (progress >= stop.start) phase = stop.phase;
  }
  return phase;
}

function resolveActiveVideo(progress: number, isMobile: boolean): number {
  if (progress < 0.18) return 0;
  if (progress < 0.48) return isMobile ? 0 : 1;
  if (progress < 0.67) return 2;
  if (progress < 0.91) return isMobile ? 2 : 3;
  return 4;
}

function localProgress(progress: number, start: number, end: number): number {
  return gsap.utils.clamp(0, 1, (progress - start) / Math.max(0.001, end - start));
}

function setPhaseRail(section: HTMLElement, phase: OperationPhase): void {
  section.querySelectorAll<HTMLElement>('[data-operation-step]').forEach((item) => {
    item.classList.toggle('is-active', item.dataset.operationStep === phase);
  });
}

function updateArrivalData(
  progress: number,
  distance: HTMLElement,
  alignment: HTMLElement,
): void {
  if (progress < 0.055) distance.textContent = '04.2 M';
  else if (progress < 0.105) distance.textContent = '02.7 M';
  else if (progress < 0.155) distance.textContent = '01.2 M';
  else distance.textContent = '00.0 M';

  const locked = progress >= 0.165;
  alignment.textContent = locked ? 'LOCKED' : 'ACTIVE';
  alignment.classList.toggle('is-locked', locked);
}

function preloadNearSection(section: HTMLElement, scrubbers: VideoScrubber[]): void {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      scrubbers[0]?.load();
      scrubbers[1]?.load();
      observer.disconnect();
    },
    { rootMargin: '180% 0px' },
  );
  observer.observe(section);
}

export function initTerminalDeparture(options: TerminalDepartureOptions): void {
  const section = document.querySelector<HTMLElement>('#terminal-departure');
  const stage = document.querySelector<HTMLElement>('#terminal-departure-stage');
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.operation-video'));
  const intro = document.querySelector<HTMLElement>('#operation-intro');
  const eyebrow = document.querySelector<HTMLElement>('.operation-eyebrow');
  const titleLines = Array.from(document.querySelectorAll<HTMLElement>('.operation-title-mask > span'));
  const introCopy = document.querySelector<HTMLElement>('.operation-intro-copy');
  const arrivalData = document.querySelector<HTMLElement>('#operation-arrival-data');
  const alignment = document.querySelector<HTMLElement>('#operation-alignment');
  const distance = document.querySelector<HTMLElement>('#operation-distance');
  const shipmentData = document.querySelector<HTMLElement>('#operation-shipment-data');
  const shipmentRows = Array.from(document.querySelectorAll<HTMLElement>('#operation-shipment-data dl > div'));
  const routeCopy = document.querySelector<HTMLElement>('#operation-route-copy');
  const handlingCopy = document.querySelector<HTMLElement>('#operation-handling-copy');
  const visionCopy = document.querySelector<HTMLElement>('#operation-vision-copy');
  const scaleCopy = document.querySelector<HTMLElement>('#operation-scale-copy');
  const statementLines = Array.from(document.querySelectorAll<HTMLElement>('.operation-statement-mask > span'));
  const livePanel = document.querySelector<HTMLElement>('#operation-live-panel');
  const dispatchMeta = document.querySelector<HTMLElement>('#operation-dispatch-meta');
  const phaseRail = document.querySelector<HTMLElement>('#operation-phase-rail');
  const floorSystem = document.querySelector<SVGSVGElement>('#operation-floor-system');
  const dockGuides = document.querySelector<SVGGElement>('.operation-dock-guides');
  const routeFloor = document.querySelector<SVGGElement>('.operation-route-floor');
  const routePath = document.querySelector<SVGPathElement>('#operation-route-path');
  const trajectory = document.querySelector<SVGGElement>('.operation-trajectory');
  const scanPlane = document.querySelector<HTMLElement>('#operation-scan-plane');
  const scanContour = document.querySelector<HTMLElement>('#operation-scan-contour');
  const visionTarget = document.querySelector<HTMLElement>('#operation-vision-target');
  const occlusion = document.querySelector<HTMLElement>('#operation-occlusion');
  const entryBlackout = document.querySelector<HTMLElement>('#terminal-departure-entry-blackout');
  const entryLine = document.querySelector<HTMLElement>('#terminal-departure-entry-line');
  const entryLineShape = document.querySelector<HTMLElement>('#terminal-departure-entry-line span');

  if (
    !section || !stage || videos.length !== 5 || !intro || !eyebrow || titleLines.length !== 2 ||
    !introCopy || !arrivalData || !alignment || !distance || !shipmentData || shipmentRows.length !== 5 ||
    !routeCopy || !handlingCopy || !visionCopy || !scaleCopy || statementLines.length !== 2 ||
    !livePanel || !dispatchMeta || !phaseRail || !floorSystem || !dockGuides || !routeFloor ||
    !routePath || !trajectory || !scanPlane || !scanContour || !visionTarget || !occlusion ||
    !entryBlackout || !entryLine || !entryLineShape
  ) return;

  const isMobile = window.innerWidth <= 760;
  const scrubbers = videos.map(createVideoScrubber);
  preloadNearSection(section, isMobile ? [scrubbers[0]!, scrubbers[2]!] : scrubbers);

  const primeTouchPlayback = (): void => {
    videos.forEach((video) => {
      if (video.preload !== 'auto') return;
      const resumeAt = video.currentTime;
      const promise = video.play();
      if (!promise) return;
      void promise.then(() => { video.pause(); video.currentTime = resumeAt; }).catch(() => undefined);
    });
  };
  window.addEventListener('touchstart', primeTouchPlayback, { once: true, passive: true });

  if (options.reducedMotion) {
    scrubbers[3]?.load();
    videos.forEach((video, index) => { video.style.opacity = index === 3 ? '1' : '0'; });
    stage.style.visibility = 'visible';
    section.dataset.operationPhase = 'scale';
    section.dataset.activeVideo = '3';
    section.dataset.storyProgress = '0.8500';
    return;
  }

  gsap.set(stage, { visibility: 'hidden' });
  gsap.set(videos, { opacity: 0, scale: 1.018, filter: 'brightness(.68) saturate(.86)' });
  gsap.set(videos[0], { opacity: 0.12 });
  gsap.set(intro, { opacity: 0 });
  gsap.set(eyebrow, { opacity: 0, letterSpacing: '0.55em', y: 8 });
  gsap.set(titleLines, { yPercent: 130 });
  gsap.set(introCopy, { opacity: 0, filter: 'blur(5px)', y: 12 });
  gsap.set([arrivalData, shipmentData, routeCopy, handlingCopy, visionCopy, scaleCopy, livePanel, dispatchMeta], { opacity: 0 });
  gsap.set(shipmentRows, { opacity: 0, x: -8 });
  gsap.set(statementLines, { yPercent: 130 });
  gsap.set(floorSystem, { opacity: 0 });
  gsap.set([dockGuides, routeFloor, trajectory], { opacity: 0 });
  const routeLength = routePath.getTotalLength();
  gsap.set(routePath, { strokeDasharray: routeLength, strokeDashoffset: routeLength });
  gsap.set(scanPlane, { opacity: 0, xPercent: -180 });
  gsap.set(scanContour, { opacity: 0 });
  gsap.set(visionTarget, { opacity: 0, scale: 0.94 });
  gsap.set(occlusion, { opacity: 0, xPercent: -130 });
  gsap.set(entryBlackout, { opacity: 0.76 });
  gsap.set(entryLine, { opacity: 1 });
  gsap.set(entryLineShape, { width: '82vw', scaleY: 1 });
  gsap.set(phaseRail, { opacity: 0, y: 8 });

  let loadedTier = 1;
  const story = { progress: 0 };

  const syncStory = (): void => {
    const progress = story.progress;
    const activeVideo = resolveActiveVideo(progress, isMobile);
    const phase = resolvePhase(progress);

    if (progress >= 0.3 && loadedTier < 2) {
      scrubbers[2]?.load();
      loadedTier = 2;
    }
    if (progress >= 0.58 && loadedTier < 3) {
      if (!isMobile) scrubbers[3]?.load();
      scrubbers[4]?.load();
      loadedTier = 3;
    }

    // VIDEO 01 is intentionally scrubbed in reverse: forward page scroll now
    // moves the truck back toward the loading bay, while reverse scroll remains
    // a physically coherent departure from the dock.
    scrubbers[0]?.setProgress(1 - localProgress(progress, 0, isMobile ? 0.48 : 0.18));
    scrubbers[1]?.setProgress(localProgress(progress, 0.18, 0.48));
    scrubbers[2]?.setProgress(localProgress(progress, 0.48, isMobile ? 0.91 : 0.67));
    scrubbers[3]?.setProgress(localProgress(progress, 0.67, 0.91));
    scrubbers[4]?.setProgress(localProgress(progress, 0.91, 1));

    updateArrivalData(progress, distance, alignment);
    section.dataset.storyProgress = progress.toFixed(4);
    section.dataset.activeVideo = String(activeVideo);
    if (section.dataset.operationPhase !== phase) {
      section.dataset.operationPhase = phase;
      setPhaseRail(section, phase);
    }
  };

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 3.2 : 3.4))}`,
      scrub: isMobile ? 0.3 : 0.48,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => { stage.style.visibility = 'visible'; },
      onEnterBack: () => { stage.style.visibility = 'visible'; },
      onLeave: () => { story.progress = 1; syncStory(); },
      onLeaveBack: () => {
        stage.style.visibility = 'hidden';
        story.progress = 0;
        syncStory();
      },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) stage.style.visibility = 'visible';
      },
    },
  });

  timeline
    .to(story, { progress: 1, duration: 1, ease: 'none', onUpdate: syncStory }, 0)

    // Technical line -> physical floor guidance -> dock arrival.
    .to(videos[0], { opacity: 1, scale: 1, filter: 'brightness(.9) saturate(.92)', duration: 0.095, ease: 'power2.out' }, 0)
    .to(entryBlackout, { opacity: 0, duration: 0.1, ease: 'power2.out' }, 0)
    .to(entryLineShape, {
      y: '31vh',
      width: '44vw',
      scaleY: 0.42,
      duration: 0.072,
      ease: 'power3.inOut',
    }, 0)
    .to(floorSystem, { opacity: 1, duration: 0.065 }, 0.025)
    .to(dockGuides, { opacity: 0.72, duration: 0.065 }, 0.032)
    .to(entryLine, { opacity: 0, duration: 0.038 }, 0.062)
    .to(intro, { opacity: 1, duration: 0.03 }, 0.074)
    .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '0.32em', duration: 0.055 }, 0.074)
    .to(titleLines, { yPercent: 0, duration: 0.065, stagger: 0.012, ease: 'power3.out' }, 0.081)
    .to(introCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.048 }, 0.103)
    .to(arrivalData, { opacity: 1, y: 0, duration: 0.04 }, 0.109)
    .to(phaseRail, { opacity: 1, y: 0, duration: 0.04 }, 0.115)
    .to([intro, arrivalData], { opacity: 0, y: -9, duration: 0.025 }, 0.165)
    .to(dockGuides, { opacity: 0.16, duration: 0.025 }, 0.165)

    // Structural-column hidden edit into the scanning tunnel.
    .to(occlusion, { opacity: 1, xPercent: 130, duration: 0.045, ease: 'power2.inOut' }, 0.16)
    .set(videos[0], { opacity: isMobile ? 1 : 0 }, 0.18)
    .set(videos[1], { opacity: isMobile ? 0 : 1, scale: 1.01, filter: 'brightness(.82) saturate(.88)' }, 0.18)
    .to(occlusion, { opacity: 0, xPercent: 210, duration: 0.03 }, 0.19)
    .to(shipmentData, { opacity: 1, duration: 0.035 }, 0.205)
    .to(scanPlane, { opacity: 1, xPercent: -70, duration: 0.025 }, 0.205)
    .to(scanPlane, { xPercent: 105, duration: 0.12, ease: 'none' }, 0.21)
    .to(scanContour, { opacity: 0.54, duration: 0.035 }, 0.235)
    .to(scanContour, { opacity: 0, duration: 0.035 }, 0.31)
    .to(scanPlane, { opacity: 0, duration: 0.025 }, 0.325)
    .to(shipmentRows, { opacity: 1, x: 0, duration: 0.025, stagger: 0.018 }, 0.215)

    // Destination label -> route -> floor navigation.
    .to(shipmentData, { opacity: 0.34, duration: 0.025 }, 0.345)
    .to(routeCopy, { opacity: 1, y: 0, duration: 0.035 }, 0.35)
    .to(routeFloor, { opacity: 1, duration: 0.03 }, 0.35)
    .to(routePath, { strokeDashoffset: 0, duration: 0.1, ease: 'power2.out' }, 0.35)
    .to([shipmentData, routeCopy], { opacity: 0, y: -7, duration: 0.025 }, 0.455)

    // Dark machinery edge conceals the cut into cargo handling.
    .to(occlusion, { opacity: 1, xPercent: -130, duration: 0 }, 0.445)
    .to(occlusion, { xPercent: 130, duration: 0.055 }, 0.445)
    .set([videos[0], videos[1]], { opacity: 0 }, 0.48)
    .set(videos[2], { opacity: 1, scale: 1.01, filter: 'brightness(.86) saturate(.9)' }, 0.48)
    .to(occlusion, { opacity: 0, xPercent: 210, duration: 0.025 }, 0.49)
    .to(handlingCopy, { opacity: 1, duration: 0.035 }, 0.505)
    .to(trajectory, { opacity: 0.78, duration: 0.04 }, 0.5)
    .to(trajectory, { opacity: 0.24, duration: 0.13, ease: 'none' }, 0.53)
    .to(handlingCopy, { opacity: 0, y: -8, duration: 0.025 }, 0.64)

    // Vision targeting and localized cargo verification; the structural edge hides the master switch.
    .to(occlusion, { opacity: 1, xPercent: -130, duration: 0 }, 0.645)
    .to(occlusion, { xPercent: 130, duration: 0.05 }, 0.645)
    .set(videos[2], { opacity: isMobile ? 1 : 0 }, 0.67)
    .set(videos[3], { opacity: isMobile ? 0 : 1, scale: 1.018, filter: 'brightness(.72) saturate(.82)' }, 0.67)
    .to(occlusion, { opacity: 0, xPercent: 210, duration: 0.025 }, 0.68)
    .to(visionCopy, { opacity: 1, duration: 0.035 }, 0.67)
    .to(visionTarget, { opacity: 1, scale: 1, duration: 0.045, ease: 'power3.out' }, 0.675)
    .to(trajectory, { opacity: 0, duration: 0.025 }, 0.7)
    .to([visionCopy, visionTarget], { opacity: 0, duration: 0.025 }, 0.755)

    // The same terminal master pulls back to reveal industrial scale.
    .to(videos[3], { scale: 1, filter: 'brightness(.9) saturate(.88)', duration: 0.11, ease: 'none' }, 0.78)
    .to(scaleCopy, { opacity: 1, duration: 0.03 }, 0.79)
    .to(statementLines, { yPercent: 0, duration: 0.055, stagger: 0.012, ease: 'power3.out' }, 0.795)
    .to(livePanel, { opacity: 1, y: 0, duration: 0.04 }, 0.81)
    .to([scaleCopy, livePanel], { opacity: 0, y: -9, duration: 0.025 }, 0.895)

    // Final dispatch: one truck leaves the operation for the future journey section.
    .to(occlusion, { opacity: 1, xPercent: -130, duration: 0 }, 0.89)
    .to(occlusion, { xPercent: 130, duration: 0.045 }, 0.89)
    .set([videos[2], videos[3]], { opacity: 0 }, 0.91)
    .set(videos[4], { opacity: 1, scale: 1.006, filter: 'brightness(.82) saturate(.88)' }, 0.91)
    .to(videos[4], { scale: 1, filter: 'brightness(1) saturate(1)', duration: 0.085, ease: 'none' }, 0.91)
    .to(occlusion, { opacity: 0, xPercent: 210, duration: 0.022 }, 0.92)
    .to(dispatchMeta, { opacity: 1, duration: 0.03 }, 0.925)
    .to([floorSystem, phaseRail], { opacity: 0, duration: 0.035 }, 0.945)
    .to(dispatchMeta, { opacity: 0, duration: 0.025 }, 0.975);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
